// EDIT BY ALI FOR DARK MODE - Updated theme to support both light and dark modes
import {createTheme} from "@mui/material/styles";
import {
  ALPHA_PURPLE,
  CSS_PRIMARY_DARK_MODE_VAR,
  CSS_PRIMARY_LIGHT_MODE_VAR,
  CSS_PRIMARY_VAR,
  DARK_MODE_PRIMARY,
  DARK_MODE_PRIMARY_HOVER,
  LIGHT_MODE_PRIMARY,
  buildBannerPalette,
} from "./theme/colorTokens";

// EDIT BY ALI FOR DARK MODE - Function to create theme based on mode
export const createAlphaSearchTheme = (mode = "light") => {
  const isDark = mode === "dark";
  const pageBackground = isDark ? "#121212" : "#FFFFFF";
  
  return createTheme({
    palette: {
      mode: mode,
      // EDIT BY ALI FOR DARK MODE - Background and text colors adapt to mode
      background: {
        default: pageBackground,
        paper: isDark ? "#1E1E1E" : "#FFFFFF",
      },
      // EDIT BY ALI FOR DARK MODE - Updated text colors for better contrast
      text: {
        primary: isDark ? "#FFFFFF" : "#000000",
        secondary: isDark ? "#D0D0D0" : "#58595B",
      },
      primary: {
        main: isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY,
        light: isDark ? ALPHA_PURPLE.accentHighlight : ALPHA_PURPLE.light,
        dark: isDark ? DARK_MODE_PRIMARY_HOVER : ALPHA_PURPLE.dark,
        contrastText: "#FFFFFF"
      },
      secondary: {
        main: isDark ? ALPHA_PURPLE.surfaceTint : ALPHA_PURPLE.wash,
      },
      tertiary: {
        main: ALPHA_PURPLE.tertiary
      },
      banner: buildBannerPalette(isDark, pageBackground),
      mainPrimary: {
        prior: isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY,
      },
      mainSecondary: {
        prior: isDark ? "#FFFFFF" : "#000000",
      },
      altPalette: {
        main: "#170f4a",
        light: "#a0138e",
        dark: "#07021a",
        contrastText: "#FFFFFF"
      },
      widget: {
        dark: "#58595B",
        light: "#bdbdbd",
        main: "#6D6968",
        contrastText: "#ffffff",
        inactive: isDark ? "#3A3A3A" : "#eeeeee"
      },
      motifs: {
        money: {
          main: "#36f52c",
          light: isDark ? "#1a7a14" : "#def5dc",
          dark: "#218518",
          contrastText: "#FFFFFF"
        },
        headcount: {
          main: "#E7AB73",
          light: isDark ? "#8B6A45" : "#FFF2E6",
          dark: "#CE6300",
          contrastText: "#FFFFFF"
        },
      },
    },
    typography: {
      fontFamily: ["Plus Jakarta Sans", "sans-serif"].join(","),
      h1: {
        fontSize: "2rem",
        fontWeight: "bold",
        lineHeight: 1.2,
        letterSpacing: "-0.01562em",
        color: isDark ? "#FFFFFF" : "#000000",
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: "bold",
        lineHeight: 1.2,
        letterSpacing: "-0.00833em",
        color: isDark ? "#FFFFFF" : "#000000",
      },
      h4: {
        fontWeight: 800,
        fontSize: "22px",
        color: isDark ? "#FFFFFF" : "#000000",
        lineHeight: "28px",
      },
      h5: {
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "18px",
        lineHeight: "23px",
        color: isDark ? "#FFFFFF" : "#000000",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "-0.005px",
        color: isDark ? "#FFFFFF" : "#000000",
      },
      mainText: {
        color: isDark ? "#FFFFFF" : "#000000",
        fontWeight: 700,
        fontStyle: "normal",
        fontSize: "30px",
        lineHeight: "38px",
      },
      mainSubText : {
        fontStyle: "normal",
        fontWeight: 400,
        fontSize:"18px",
        color: isDark ? "#B0B0B0" : "#8692A6"
      },
      h6: {
        fontStyle:"normal",
        fontWeight: 600,
        fontSize:"16px",
        lineHeight:"20px",
        color: isDark ? "#B0B0B0" : "#58595B"
      },
      buttonFace : {
        color: "#FFFFFF",
        fontWeight: 600,
        fontSize: "16px",
        textTransform: "initial",
      },
      formValidation : {
        color: "#ff4444",
        fontWeight: 600,
        fontSize: "16px",
      },
      toolTipText: {
        fontWeight: 700,
        fontSize: 16,
        color: isDark ? "#FFFFFF" : "#000000",
      }
    },
    shape: {
      borderRadius: 6
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
        cdSm: 650,
        cdMd: 1240,
        cdLg: 1785,
        cdXl: 2200,
      },
    },
    spacing: (factor) => `${0.5 * factor}rem`,
    overrides: {},
    // EDIT BY ALI FOR DARK MODE - Component overrides for dark mode
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            [CSS_PRIMARY_VAR]: isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY,
            [CSS_PRIMARY_DARK_MODE_VAR]: DARK_MODE_PRIMARY,
            [CSS_PRIMARY_LIGHT_MODE_VAR]: LIGHT_MODE_PRIMARY,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          color: "inherit",
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            backgroundImage: "none",
            color: isDark ? "#FFFFFF" : "#000000",
          },
          colorInherit: {
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            backgroundImage: "none",
            color: isDark ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            color: isDark ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#2A2A2A" : "#FFFFFF",
            color: isDark ? "#FFFFFF" : "#000000",
            "&::before": {
              borderBottom: isDark ? "1px solid #444444" : "none",
            },
            "&:hover::before": {
              borderBottom: isDark ? "2px solid #666666" : "2px solid #d7d7e1",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#2A2A2A" : "#FFFFFF",
            color: isDark ? "#FFFFFF" : "#000000",
            "& fieldset": {
              borderColor: isDark ? "#444444" : "#D7DBDD",
            },
            "&:hover fieldset": {
              borderColor: isDark ? "#666666" : "#D7DBDD",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: isDark ? "#B0B0B0" : "#000000",
            "&.Mui-selected": {
              color: isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: isDark ? "#B0B0B0" : "#000000",
            "&.Mui-checked": {
              color: isDark ? DARK_MODE_PRIMARY : LIGHT_MODE_PRIMARY,
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            color: isDark ? "#FFFFFF" : "#000000",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
            color: isDark ? "#FFFFFF" : "#000000",
            "&:hover": {
              backgroundColor: isDark ? "#2A2A2A" : "#F5F5F5",
            },
          },
        },
      },
    },
  });
};

// EDIT BY ALI FOR DARK MODE - Export default light theme for backward compatibility
export const alphaSearchTheme = createAlphaSearchTheme("light");